import { useMemo, useState } from 'react'
import { useApiFetch } from '@/hooks/useApiFetch'
import { useApiMutation } from '@/hooks/useApiMutation'
import { useToast } from '@/components/ToastContainer'
import {
  Tabs,
  Table,
  Pagination,
  StatCard,
  DateRangePicker,
  Input,
  Textarea,
  Select,
  Button,
  Alert,
  Drawer,
  Modal,
  EmptyState,
  Card,
  type TableColumn,
  type TabItem,
  formatDate,
  formatCurrency,
  formatNumber,
} from '@/ui'
import { StatusBadge } from '@/components/StatusBadge'
import { SHARED_RANGE_PRESETS_WITH_ALL, ALL_PRESET_KEY } from '@/lib/dateRange'
import { ExternalLink } from 'lucide-react'
import styles from './Toss.module.css'

// ============================================================
// 타입
// ============================================================

interface PaginationMeta {
  page: number
  pageSize: number
  total: number
  totalPages: number
  hasNext: boolean
  hasPrev: boolean
}

interface TossPaymentRow {
  id: string
  user: { id: string | null; email: string | null; name: string | null }
  type: string
  amount: number
  creditsGranted: number | null
  status: string
  orderId: string | null
  paymentKey: string | null
  receiptUrl: string | null
  createdAt: string
}

interface PaymentsResponse {
  payments: TossPaymentRow[]
  pagination: PaginationMeta
  summary: { totalAmount: number; refundedAmount: number; count: number; refundRate: number }
}

interface TossPaymentLive {
  paymentKey: string
  orderId: string
  status: string
  totalAmount: number
  receipt: { url: string } | null
  cancels: Array<{ cancelAmount: number; cancelReason: string; canceledAt: string }> | null
}

interface SearchResponse {
  tossPayment: TossPaymentLive
  dbPayment: TossPaymentRow | null
}

interface SlotCounts {
  universal: number
  invitation: number
  thankyou: number
}

interface RefundResponse {
  paymentId: string
  refundedAmount: number
  fullRefund: boolean
  creditsDeducted: number
  slotsRevoked?: SlotCounts
  slotShortfall?: SlotCounts
  bonusRevoked?: number
}

interface AbortResponse {
  paymentId: string
  previousStatus: string
  newStatus: 'FAILED'
}

// ============================================================
// 공통 유틸
// ============================================================

const TYPE_LABEL: Record<string, string> = {
  INVITATION_MAIN: '청첩장',
  INVITATION_PACKAGE: '청첩장',
  STUDIO_PHOTOS_10: 'AI 스튜디오 +10',
  STUDIO_PHOTOS_20: 'AI 스튜디오 +20',
  STUDIO_PHOTOS_30: 'AI 스튜디오 +30',
  SNAP_MAIN: 'AI 스냅',
  SNAP_PHOTOS_10: 'AI 스냅 +10',
  SNAP_PHOTOS_20: 'AI 스냅 +20',
}

function dateToFromIso(d: string | null | undefined): string | undefined {
  return d ? `${d}T00:00:00.000Z` : undefined
}
function dateToEndIso(d: string | null | undefined): string | undefined {
  return d ? `${d}T23:59:59.999Z` : undefined
}
function defaultDate(daysAgo: number): string {
  const d = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000)
  return d.toISOString().slice(0, 10)
}
function todayDate(): string {
  return new Date().toISOString().slice(0, 10)
}

// 빠른검색 프리셋 — admin 공용(SHARED_RANGE_PRESETS_WITH_ALL). '전체'는 기간 해제.

// ============================================================
// 메인 페이지
// ============================================================

const TAB_ITEMS: TabItem[] = [
  { key: 'list', label: '결제 목록' },
  { key: 'search', label: '결제 검색' },
]

export function Toss() {
  const [tab, setTab] = useState<string>('list')

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <h1 className={styles.title}>토스 결제</h1>
        <p className={styles.subtitle}>DB 기반 목록 조회 + 토스 라이브 검색 + 어드민 강제 환불</p>
      </header>
      <Tabs items={TAB_ITEMS} value={tab} onChange={setTab} variant="card" />
      {tab === 'list' && <PaymentListTab />}
      {tab === 'search' && <PaymentSearchTab />}
    </div>
  )
}

// ============================================================
// 1) 결제 목록 탭
// ============================================================

function PaymentListTab() {
  const [draftFrom, setDraftFrom] = useState<string>(() => defaultDate(30))
  const [draftTo, setDraftTo] = useState<string>(() => todayDate())
  const [draftStatus, setDraftStatus] = useState<string>('')
  const [draftSearch, setDraftSearch] = useState<string>('')
  const [appliedFrom, setAppliedFrom] = useState<string>(draftFrom)
  const [appliedTo, setAppliedTo] = useState<string>(draftTo)
  const [appliedStatus, setAppliedStatus] = useState<string>('')
  const [appliedSearch, setAppliedSearch] = useState<string>('')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<TossPaymentRow | null>(null)

  const params = useMemo(() => {
    const p = new URLSearchParams()
    p.set('page', String(page))
    p.set('pageSize', '20')
    const f = dateToFromIso(appliedFrom)
    const t = dateToEndIso(appliedTo)
    if (f) p.set('from', f)
    if (t) p.set('to', t)
    if (appliedStatus) p.set('status', appliedStatus)
    if (appliedSearch.trim()) p.set('search', appliedSearch.trim())
    return p
  }, [page, appliedFrom, appliedTo, appliedStatus, appliedSearch])

  const path = `/api/admin/toss/payments?${params.toString()}`
  const { data, isLoading, error, refetch } = useApiFetch<PaymentsResponse>(path)

  const handleSearch = () => {
    setAppliedFrom(draftFrom)
    setAppliedTo(draftTo)
    setAppliedStatus(draftStatus)
    setAppliedSearch(draftSearch)
    setPage(1)
  }

  const handleRangeChange = (
    range: { from: string | null; to: string | null },
    meta: { presetKey: string | null },
  ) => {
    // '전체' 프리셋 = 기간 필터 해제 후 즉시 적용
    if (meta.presetKey === ALL_PRESET_KEY) {
      setDraftFrom('')
      setDraftTo('')
      setAppliedFrom('')
      setAppliedTo('')
      setPage(1)
      return
    }
    if (!range.from || !range.to) return
    // 프리셋(빠른검색)은 즉시 적용, 커스텀 범위는 draft 만 갱신 후 '검색'으로 확정.
    setDraftFrom(range.from)
    setDraftTo(range.to)
    if (meta.presetKey) {
      setAppliedFrom(range.from)
      setAppliedTo(range.to)
      setPage(1)
    }
  }

  const columns: TableColumn<TossPaymentRow>[] = [
    { key: 'id', label: 'ID', render: (r) => <code className={styles.codeText}>{r.id.slice(0, 10)}</code>, width: '110px' },
    { key: 'user', label: '유저', render: (r) => r.user.name ?? r.user.email ?? '—', width: '160px' },
    { key: 'type', label: '상품', render: (r) => TYPE_LABEL[r.type] ?? r.type, width: '140px' },
    { key: 'amount', label: '금액', render: (r) => formatCurrency(r.amount), align: 'right' as const, width: '110px' },
    { key: 'creditsGranted', label: '크레딧', render: (r) => r.creditsGranted ?? '—', align: 'right' as const, width: '70px' },
    { key: 'status', label: '상태', render: (r) => <StatusBadge status={r.status} />, width: '100px' },
    { key: 'orderId', label: '주문ID', render: (r) => r.orderId ? <code className={styles.codeText}>{r.orderId.slice(0, 14)}</code> : '—', width: '160px' },
    { key: 'createdAt', label: '일시', render: (r) => formatDate(r.createdAt, 'yyyy.M.d HH:mm'), width: '130px' },
  ]

  return (
    <div className={styles.tabContent}>
      <div className={styles.statGrid}>
        <StatCard label="총 매출" value={formatCurrency(data?.summary.totalAmount ?? 0)} />
        <StatCard label="결제 건수" value={`${formatNumber(data?.summary.count ?? 0)}건`} />
        <StatCard label="환불액" value={formatCurrency(data?.summary.refundedAmount ?? 0)} />
        <StatCard label="환불률" value={`${((data?.summary.refundRate ?? 0) * 100).toFixed(1)}%`} />
      </div>

      <div className={styles.filters}>
        <DateRangePicker
          size="sm"
          value={{ from: draftFrom || null, to: draftTo || null }}
          presets={SHARED_RANGE_PRESETS_WITH_ALL}
          onChange={handleRangeChange}
        />
        <Select
          size="sm"
          value={draftStatus}
          onChange={(e) => setDraftStatus(e.target.value)}
          options={[
            { value: '', label: '전체 상태' },
            { value: 'PENDING', label: 'PENDING' },
            { value: 'COMPLETED', label: 'COMPLETED' },
            { value: 'FAILED', label: 'FAILED' },
            { value: 'REFUNDED', label: 'REFUNDED' },
          ]}
        />
        <Input
          size="sm"
          value={draftSearch}
          onChange={(e) => setDraftSearch(e.target.value)}
          placeholder="orderId / 이메일 / 이름"
        />
        <Button variant="primary" size="sm" onClick={handleSearch} disabled={isLoading}>검색</Button>
        <Button variant="secondary" size="sm" onClick={() => refetch()} disabled={isLoading}>새로고침</Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {!isLoading && data && data.payments.length === 0 ? (
        <EmptyState title="조회된 결제가 없습니다" description="기간·필터를 조정해보세요" />
      ) : (
        <Table<TossPaymentRow>
          columns={columns}
          data={data?.payments ?? []}
          rowKey={(r) => r.id}
          isLoading={isLoading}
          onRowClick={(r) => setSelected(r)}
        />
      )}

      {data?.pagination && data.pagination.totalPages > 1 && (
        <Pagination current={data.pagination.page} total={data.pagination.totalPages} onChange={setPage} size="sm" />
      )}

      <Drawer open={!!selected} onClose={() => setSelected(null)} title="토스 결제 상세" size="md">
        {selected && <PaymentDetail payment={selected} onRefunded={() => { setSelected(null); refetch() }} />}
      </Drawer>
    </div>
  )
}

// ============================================================
// 결제 상세 — Drawer 내부에서 토스 라이브 정보 + 환불 버튼
// ============================================================

function PaymentDetail({ payment, onRefunded }: { payment: TossPaymentRow; onRefunded: () => void }) {
  const [refundOpen, setRefundOpen] = useState(false)
  const [abortOpen, setAbortOpen] = useState(false)
  const livePath = payment.paymentKey
    ? `/api/admin/toss/payments/search?paymentKey=${encodeURIComponent(payment.paymentKey)}`
    : null
  const { data: liveData } = useApiFetch<SearchResponse>(livePath)

  return (
    <div className={styles.detail}>
      <div className={styles.metaGrid}>
        <Meta label="결제 ID" value={payment.id} />
        <Meta label="유저" value={payment.user.name ?? payment.user.email ?? '—'} />
        <Meta label="상품" value={TYPE_LABEL[payment.type] ?? payment.type} />
        <Meta label="금액" value={formatCurrency(payment.amount)} />
        <Meta label="크레딧" value={payment.creditsGranted == null ? '—' : `${payment.creditsGranted}`} />
        <Meta label="상태" value={payment.status} />
        <Meta label="주문 ID" value={payment.orderId ?? '—'} />
        <Meta label="결제키" value={payment.paymentKey ? payment.paymentKey.slice(0, 24) + '…' : '—'} />
        <Meta label="일시" value={formatDate(payment.createdAt, 'yyyy.M.d HH:mm:ss')} />
      </div>

      {payment.receiptUrl && (
        <Button variant="secondary" size="sm" onClick={() => window.open(payment.receiptUrl!, '_blank')}>
          <ExternalLink size={14} /> 토스 매출전표
        </Button>
      )}

      {liveData?.tossPayment.cancels && liveData.tossPayment.cancels.length > 0 && (
        <div>
          <h3 className={styles.sectionTitle}>환불 이력 (토스 라이브)</h3>
          <div className={styles.itemList}>
            {liveData.tossPayment.cancels.map((cancel, i) => (
              <Card key={i}>
                <div className={styles.itemBody}>
                  <div className={styles.itemMetaRow}>
                    <span>금액: <strong>{formatCurrency(cancel.cancelAmount)}</strong></span>
                    <span>{formatDate(cancel.canceledAt, 'yyyy.M.d HH:mm')}</span>
                  </div>
                  <div className={styles.muted}>{cancel.cancelReason}</div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {payment.status === 'COMPLETED' && (
        <Button variant="danger" onClick={() => setRefundOpen(true)}>
          어드민 환불 처리
        </Button>
      )}

      {payment.status === 'PENDING' && (
        <Button variant="secondary" onClick={() => setAbortOpen(true)}>
          PENDING 강제 정리
        </Button>
      )}

      <RefundModal
        open={refundOpen}
        payment={payment}
        onClose={() => setRefundOpen(false)}
        onSuccess={() => { setRefundOpen(false); onRefunded() }}
      />

      <AbortModal
        open={abortOpen}
        payment={payment}
        onClose={() => setAbortOpen(false)}
        onSuccess={() => { setAbortOpen(false); onRefunded() }}
      />
    </div>
  )
}

// ============================================================
// 환불 모달
// ============================================================

function RefundModal({
  open,
  payment,
  onClose,
  onSuccess,
}: {
  open: boolean
  payment: TossPaymentRow
  onClose: () => void
  onSuccess: () => void
}) {
  const { addToast } = useToast()
  const [reason, setReason] = useState('')
  const [amount, setAmount] = useState<string>(String(payment.amount))

  const path = `/api/admin/toss/payments/${encodeURIComponent(payment.id)}/refund`
  const { trigger, isLoading } = useApiMutation<RefundResponse, Record<string, unknown>>(path, 'POST')

  const handleConfirm = async () => {
    if (!reason.trim()) {
      addToast({ type: 'error', message: '사유를 입력해주세요' })
      return
    }
    const amt = Number(amount)
    if (!Number.isFinite(amt) || amt <= 0 || amt > payment.amount) {
      addToast({ type: 'error', message: `환불 금액은 1~${payment.amount}원 사이여야 합니다` })
      return
    }
    const body: Record<string, unknown> = { reason: reason.trim() }
    if (amt !== payment.amount) body.amount = amt
    const result = await trigger(body)
    if (result) {
      if (result.fullRefund) {
        const slots = result.slotsRevoked
        const slotTotal = slots ? slots.universal + slots.invitation + slots.thankyou : 0
        const parts = [`크레딧 ${result.creditsDeducted}`]
        if (result.bonusRevoked) parts.push(`보너스 ${result.bonusRevoked}`)
        if (slotTotal > 0) parts.push(`슬롯 ${slotTotal}`)
        addToast({
          type: 'success',
          message: `전액 환불 완료 (${formatCurrency(result.refundedAmount)} / ${parts.join(' + ')} 회수)`,
        })
        // 이미 사용된 슬롯은 회수 불가 → 운영자 확인 경고
        const shortfall = result.slotShortfall
        const shortfallTotal = shortfall ? shortfall.universal + shortfall.invitation + shortfall.thankyou : 0
        if (shortfallTotal > 0) {
          addToast({
            type: 'error',
            message: `이미 사용된 슬롯 ${shortfallTotal}개는 회수하지 못했습니다 (운영자 확인 필요)`,
          })
        }
      } else {
        addToast({
          type: 'success',
          message: `부분 환불 완료 (${formatCurrency(result.refundedAmount)})`,
        })
      }
      onSuccess()
    } else {
      addToast({ type: 'error', message: '환불 실패 — 콘솔/네트워크 탭 확인' })
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="어드민 환불 처리" size="sm">
      <div className={styles.actionDialog}>
        <div className={styles.muted}>{payment.id} · {payment.user.name ?? payment.user.email ?? '—'} · {formatCurrency(payment.amount)}</div>
        <Alert variant="warning">
          전액 환불 시 결제 REFUNDED + 크레딧·보너스·미사용 슬롯 회수. 이미 활성화에 사용된 슬롯은 회수되지 않습니다. 부분 환불은 토스만 처리되며 DB 상태는 유지됩니다.
        </Alert>
        <Input
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          label={`환불 금액 (최대 ${formatCurrency(payment.amount)})`}
          type="number"
        />
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          label="사유"
          placeholder="환불 사유를 입력하세요 (감사 로그·고객 응대용)"
          rows={3}
        />
        <div className={styles.actionDialogButtons}>
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>취소</Button>
          <Button variant="danger" onClick={handleConfirm} loading={isLoading}>환불 실행</Button>
        </div>
      </div>
    </Modal>
  )
}

// ============================================================
// 2) 결제 검색 탭
// ============================================================

function PaymentSearchTab() {
  const [mode, setMode] = useState<'paymentKey' | 'orderId'>('paymentKey')
  const [input, setInput] = useState('')
  const [submitted, setSubmitted] = useState<{ key: string; mode: 'paymentKey' | 'orderId' } | null>(null)

  const path = submitted
    ? `/api/admin/toss/payments/search?${submitted.mode === 'paymentKey' ? 'paymentKey' : 'orderId'}=${encodeURIComponent(submitted.key)}`
    : null
  const { data, isLoading, error, refetch } = useApiFetch<SearchResponse>(path)

  const handleSubmit = () => {
    if (!input.trim()) return
    setSubmitted({ key: input.trim(), mode })
  }

  return (
    <div className={styles.tabContent}>
      <div className={styles.searchRow}>
        <Select
          size="md"
          value={mode}
          onChange={(e) => setMode(e.target.value as 'paymentKey' | 'orderId')}
          options={[
            { value: 'paymentKey', label: '결제키 (paymentKey)' },
            { value: 'orderId', label: '주문번호 (orderId)' },
          ]}
        />
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={mode === 'paymentKey' ? '예: tgen_2024…' : '예: order_user_…'}
          size="md"
          onKeyDown={(e) => { if (e.key === 'Enter' && input.trim()) handleSubmit() }}
        />
        <Button variant="primary" onClick={handleSubmit} disabled={!input.trim() || isLoading}>
          검색
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {!submitted && <EmptyState title="결제키 또는 주문번호를 입력하세요" />}

      {data && (
        <div className={styles.detail}>
          <h2 className={styles.sectionTitle}>토스 라이브 결과</h2>
          <div className={styles.metaGrid}>
            <Meta label="결제키" value={data.tossPayment.paymentKey} />
            <Meta label="주문ID" value={data.tossPayment.orderId} />
            <Meta label="상태" value={data.tossPayment.status} />
            <Meta label="총액" value={formatCurrency(data.tossPayment.totalAmount)} />
          </div>

          {data.tossPayment.receipt?.url && (
            <Button variant="secondary" size="sm" onClick={() => window.open(data.tossPayment.receipt!.url, '_blank')}>
              <ExternalLink size={14} /> 토스 매출전표
            </Button>
          )}

          {data.tossPayment.cancels && data.tossPayment.cancels.length > 0 && (
            <>
              <h3 className={styles.sectionTitle}>환불 이력</h3>
              <div className={styles.itemList}>
                {data.tossPayment.cancels.map((c, i) => (
                  <Card key={i}>
                    <div className={styles.itemBody}>
                      <div className={styles.itemMetaRow}>
                        <span><strong>{formatCurrency(c.cancelAmount)}</strong></span>
                        <span>{formatDate(c.canceledAt, 'yyyy.M.d HH:mm')}</span>
                      </div>
                      <div className={styles.muted}>{c.cancelReason}</div>
                    </div>
                  </Card>
                ))}
              </div>
            </>
          )}

          <h2 className={styles.sectionTitle}>DB 매칭</h2>
          {data.dbPayment ? (
            <Card>
              <div className={styles.itemBody}>
                <div className={styles.metaGrid}>
                  <Meta label="결제 ID" value={data.dbPayment.id} />
                  <Meta label="유저" value={data.dbPayment.user.name ?? data.dbPayment.user.email ?? '—'} />
                  <Meta label="상품" value={TYPE_LABEL[data.dbPayment.type] ?? data.dbPayment.type} />
                  <Meta label="DB 상태" value={data.dbPayment.status} />
                  <Meta label="크레딧" value={data.dbPayment.creditsGranted == null ? '—' : `${data.dbPayment.creditsGranted}`} />
                  <Meta label="일시" value={formatDate(data.dbPayment.createdAt, 'yyyy.M.d HH:mm')} />
                </div>
                {data.dbPayment.status === 'COMPLETED' && (
                  <SearchTabRefundButton payment={data.dbPayment} onSuccess={() => refetch()} />
                )}
                {data.dbPayment.status === 'PENDING' && (
                  <SearchTabAbortButton payment={data.dbPayment} onSuccess={() => refetch()} />
                )}
              </div>
            </Card>
          ) : (
            <Alert variant="warning">DB에 매칭되는 결제가 없습니다 — 토스에는 있으나 우리 DB에 기록 안 된 결제일 수 있습니다.</Alert>
          )}
        </div>
      )}
    </div>
  )
}

function SearchTabRefundButton({ payment, onSuccess }: { payment: TossPaymentRow; onSuccess: () => void }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button variant="danger" size="sm" onClick={() => setOpen(true)}>
        어드민 환불 처리
      </Button>
      <RefundModal open={open} payment={payment} onClose={() => setOpen(false)} onSuccess={() => { setOpen(false); onSuccess() }} />
    </>
  )
}

function SearchTabAbortButton({ payment, onSuccess }: { payment: TossPaymentRow; onSuccess: () => void }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        PENDING 강제 정리
      </Button>
      <AbortModal open={open} payment={payment} onClose={() => setOpen(false)} onSuccess={() => { setOpen(false); onSuccess() }} />
    </>
  )
}

// ============================================================
// PENDING 강제 정리 모달
// ============================================================

function AbortModal({
  open,
  payment,
  onClose,
  onSuccess,
}: {
  open: boolean
  payment: TossPaymentRow
  onClose: () => void
  onSuccess: () => void
}) {
  const { addToast } = useToast()
  const [reason, setReason] = useState('')

  const path = `/api/admin/toss/payments/${encodeURIComponent(payment.id)}/abort`
  const { trigger, isLoading } = useApiMutation<AbortResponse, Record<string, unknown>>(path, 'POST')

  const handleConfirm = async () => {
    if (!reason.trim()) {
      addToast({ type: 'error', message: '사유를 입력해주세요' })
      return
    }
    const result = await trigger({ reason: reason.trim() })
    if (result) {
      addToast({
        type: 'success',
        message: `정리 완료 (${result.previousStatus} → ${result.newStatus})`,
      })
      onSuccess()
    } else {
      addToast({ type: 'error', message: '정리 실패 — 콘솔/네트워크 탭 확인' })
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="PENDING 강제 정리" size="sm">
      <div className={styles.actionDialog}>
        <div className={styles.muted}>{payment.id} · {payment.user.name ?? payment.user.email ?? '—'} · {formatCurrency(payment.amount)}</div>
        <Alert variant="info">
          토스 측에 승인되지 않은 PENDING 결제를 폐기합니다. 토스 API는 호출되지 않으며, DB 상태를 FAILED로 마킹합니다. 크레딧/활성화는 영향 없음.
        </Alert>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          label="사유"
          placeholder="정리 사유를 입력하세요 (감사 로그용)"
          rows={3}
        />
        <div className={styles.actionDialogButtons}>
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>취소</Button>
          <Button variant="danger" onClick={handleConfirm} loading={isLoading}>정리 실행</Button>
        </div>
      </div>
    </Modal>
  )
}

// ============================================================
// 작은 헬퍼
// ============================================================

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.meta}>
      <span className={styles.metaLabel}>{label}</span>
      <span className={styles.metaValue}>{value}</span>
    </div>
  )
}

export default Toss
